import { PAKISTAN_PATHS } from "./pakistan-paths.js";

/**
 * Pakistan country outline + cartographic overlays.
 *
 * Used by the Transition33 welcome page (the AtlasPlate). All overlays
 * are optional via boolean props so the same component can render
 * "just the country" elsewhere if needed.
 *
 * Geometry (SVG viewBox 1000 × 959):
 *   - 33°N → y=284   (the gold rule passes through Islamabad)
 *   - 30°N → y=353
 *   - 36°N → y=79
 *   - Islamabad → (737, 276)
 *   - Karachi → (488, 793)
 *   - Lahore → (790, 380)
 *   - Quetta → (425, 470)
 *   - Peshawar → (693, 225)
 *
 * The 33°N gold rule visibly crossing the country and passing through
 * the Islamabad pin is the entire point of the Transition33 brand —
 * if the SVG is ever swapped/rebuilt, recalculate the y-coordinates so
 * the rule still aligns.
 *
 * Canon: docs/design-canon/gop/design_handoff_transition33_welcome.
 */

interface PakistanMapProps {
  /** Fill colour for the country shape. */
  fill?: string;
  /** Render the 33°N gold rule across the full SVG width. */
  showLat33?: boolean;
  /** Render the dashed 30°N + 36°N reference parallels. */
  showOtherParallels?: boolean;
  /** Render the Islamabad pin (outer ring + inner dot). */
  showIslamabad?: boolean;
  /** Render the "ISLAMABAD · 33°43′N" mono label next to the pin. */
  islamabadLabel?: boolean;
  /** Render Karachi / Lahore / Quetta / Peshawar markers + labels. */
  showCities?: boolean;
  /** Custom colour for the 33°N rule + Islamabad pin. Defaults to accent gold. */
  accentColor?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function PakistanMap({
  fill = "currentColor",
  showLat33 = false,
  showOtherParallels = false,
  showIslamabad = false,
  islamabadLabel = false,
  showCities = false,
  accentColor = "var(--color-accent)",
  className,
  style,
}: PakistanMapProps) {
  return (
    <svg
      viewBox="0 0 1000 959"
      preserveAspectRatio="xMidYMid meet"
      className={className}
      style={{ display: "block", ...style }}
      aria-hidden="true"
    >
      <g>
        {PAKISTAN_PATHS.map((d, i) => (
          <path key={i} d={d} fill={fill} stroke="none" strokeLinejoin="round" />
        ))}
      </g>

      {showOtherParallels && (
        <g>
          <line
            x1={0}
            x2={1000}
            y1={353}
            y2={353}
            stroke="currentColor"
            strokeWidth={0.5}
            opacity={0.25}
            strokeDasharray="4 6"
          />
          <line
            x1={0}
            x2={1000}
            y1={79}
            y2={79}
            stroke="currentColor"
            strokeWidth={0.5}
            opacity={0.25}
            strokeDasharray="4 6"
          />
        </g>
      )}

      {showLat33 && (
        <line x1={0} x2={1000} y1={284} y2={284} stroke={accentColor} strokeWidth={1.5} />
      )}

      {showIslamabad && (
        <g transform="translate(737,276)">
          <circle r={14} fill="none" stroke={accentColor} strokeWidth={1.5} opacity={0.4} />
          <circle r={6} fill={accentColor} />
          {islamabadLabel && (
            <g>
              <line x1={14} x2={36} y1={0} y2={0} stroke={accentColor} strokeWidth={1} />
              <text
                x={42}
                y={4}
                fontFamily="var(--font-mono)"
                fontSize={14}
                fill={accentColor}
                letterSpacing={1.5}
              >
                ISLAMABAD · 33°43′N
              </text>
            </g>
          )}
        </g>
      )}

      {showCities && (
        <g
          fontFamily="var(--font-mono)"
          fontSize={11}
          letterSpacing={1}
          fill="currentColor"
          opacity={0.7}
        >
          <g transform="translate(488,793)">
            <circle r={3} fill="currentColor" />
            <text x={8} y={4}>
              KARACHI
            </text>
          </g>
          <g transform="translate(790,380)">
            <circle r={3} fill="currentColor" />
            <text x={8} y={4}>
              LAHORE
            </text>
          </g>
          <g transform="translate(425,470)">
            <circle r={3} fill="currentColor" />
            <text x={8} y={4}>
              QUETTA
            </text>
          </g>
          <g transform="translate(693,225)">
            <circle r={3} fill="currentColor" />
            <text x={8} y={4}>
              PESHAWAR
            </text>
          </g>
        </g>
      )}
    </svg>
  );
}
